import {IActivity} from '../../../../models/activity';
import {ITeam} from '../../../../models/team';
import {IWeatherCondition, WeatherTypes} from '../../../../models/weatherCondition';
import {ICreateCleanUpForm} from '../index';
import s from '../index.module.scss';

interface Props {
    form: ICreateCleanUpForm;
    activities: IActivity[];
    teams: ITeam[];
    weatherConditions: IWeatherCondition[];
    programSelected: boolean;
    onChange: (updates: Partial<ICreateCleanUpForm>) => void;
}

export default function Step5Remarks({
    form,
    activities,
    teams,
    weatherConditions,
    programSelected,
    onChange,
}: Props) {
    const setWeatherValue = (
        conditionId: string,
        field: 'option_id' | 'value',
        value: string
    ) => {
        const existing = form.weather_conditions.find((wc) => wc.condition_id === conditionId);
        const updated = existing
            ? form.weather_conditions.map((wc) =>
                  wc.condition_id === conditionId ? {...wc, [field]: value || null} : wc
              )
            : [
                  ...form.weather_conditions,
                  {condition_id: conditionId, option_id: null, value: '', [field]: value},
              ];
        onChange({weather_conditions: updated});
    };

    return (
        <div className={s.stepBody}>
            {!programSelected && (
                <p className={s.warning}>
                    Select a program in Step 1 to load activities and team data.
                </p>
            )}

            <div className={s.formGroup}>
                <label>Activity</label>
                <select
                    value={form.activityId}
                    onChange={(e) => onChange({activityId: e.target.value})}
                    className={s.select}
                    disabled={!programSelected}
                >
                    <option value="">No activity</option>
                    {activities.map((a) => (
                        <option key={a.id} value={a.id}>
                            {a.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className={s.formGroup}>
                <label>Team</label>
                <select
                    value={form.team_id}
                    onChange={(e) => onChange({team_id: e.target.value})}
                    className={s.select}
                    disabled={!programSelected}
                >
                    <option value="">No team</option>
                    {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className={s.formGroup}>
                <label>Number of Participants</label>
                <input
                    type="number"
                    min="0"
                    value={form.num_of_participant}
                    onChange={(e) => onChange({num_of_participant: e.target.value})}
                    className={s.input}
                    placeholder="e.g., 25"
                />
            </div>

            <div className={s.formGroup}>
                <label>Duration</label>
                <div className={s.durationGroup}>
                    <div className={s.durationSlot}>
                        <span className={s.durationLabel}>From</span>
                        <input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="HH"
                            value={form.duration_from_hour}
                            onChange={(e) => onChange({duration_from_hour: e.target.value})}
                            className={s.durationInput}
                        />
                        <span className={s.durationColon}>:</span>
                        <input
                            type="number"
                            min="0"
                            max="59"
                            placeholder="MM"
                            value={form.duration_from_minute}
                            onChange={(e) => onChange({duration_from_minute: e.target.value})}
                            className={s.durationInput}
                        />
                    </div>
                    <div className={s.durationSlot}>
                        <span className={s.durationLabel}>To</span>
                        <input
                            type="number"
                            min="0"
                            max="23"
                            placeholder="HH"
                            value={form.duration_to_hour}
                            onChange={(e) => onChange({duration_to_hour: e.target.value})}
                            className={s.durationInput}
                        />
                        <span className={s.durationColon}>:</span>
                        <input
                            type="number"
                            min="0"
                            max="59"
                            placeholder="MM"
                            value={form.duration_to_minute}
                            onChange={(e) => onChange({duration_to_minute: e.target.value})}
                            className={s.durationInput}
                        />
                    </div>
                </div>
            </div>

            {weatherConditions.length > 0 && (
                <>
                    <div className={s.subSectionTitle}>Weather Conditions</div>
                    {weatherConditions.map((wc) => {
                        const entry = form.weather_conditions.find(
                            (e) => e.condition_id === wc.id
                        );
                        return (
                            <div key={wc.id} className={s.formGroup}>
                                <label>
                                    {wc.title}
                                    {wc.unit ? ` (${wc.unit})` : ''}
                                </label>
                                {wc.type === WeatherTypes.LIST ? (
                                    <select
                                        value={entry?.option_id ?? ''}
                                        onChange={(e) =>
                                            setWeatherValue(wc.id, 'option_id', e.target.value)
                                        }
                                        className={s.select}
                                    >
                                        <option value="">Select option</option>
                                        {wc.options.map((opt) => (
                                            <option key={opt.id} value={opt.id}>
                                                {opt.title}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="number"
                                        value={entry?.value ?? ''}
                                        onChange={(e) =>
                                            setWeatherValue(wc.id, 'value', e.target.value)
                                        }
                                        className={s.input}
                                        placeholder="Enter value"
                                    />
                                )}
                            </div>
                        );
                    })}
                </>
            )}

            <div className={s.formGroup}>
                <label>Notes</label>
                <textarea
                    value={form.notes}
                    onChange={(e) => onChange({notes: e.target.value})}
                    className={s.textarea}
                    rows={4}
                    placeholder="Any additional observations or notes..."
                />
            </div>
        </div>
    );
}
